import { useCallback, useLayoutEffect, useRef } from 'react';

import type { CraftEmailTimelineItem } from '../../../../features/craft-email-agent/types';
import { AIOutreachEmptyState } from '../AIOutreachEmptyState';
import { AIOutreachTimeline } from '../AIOutreachTimeline';

import { StyledContent, StyledOutreachContentRoot, StyledOutreachScrollColumn } from './styles';

type AIOutreachContentProps = {
  isFollowUpLoading: boolean;
  timeline: CraftEmailTimelineItem[];
  isError: boolean;
  threadId: string | null;
  scrollToTopAfterUserSend: number;
  onGenerate: () => void;
  onRetry: () => Promise<void>;
};

export const AIOutreachContent = ({
  isFollowUpLoading,
  timeline,
  isError,
  threadId,
  scrollToTopAfterUserSend,
  onGenerate,
  onRetry,
}: AIOutreachContentProps) => {
  const scrollColumnRef = useRef<HTMLDivElement>(null);
  const userMessageAnchorsRef = useRef(new Map<string, HTMLElement>());

  const setUserMessageAnchor = useCallback((id: string, el: HTMLElement | null) => {
    if (el) {
      userMessageAnchorsRef.current.set(id, el);
    } else {
      userMessageAnchorsRef.current.delete(id);
    }
  }, []);

  useLayoutEffect(() => {
    if (scrollToTopAfterUserSend < 1) {
      return;
    }
    const container = scrollColumnRef.current;
    if (!container) {
      return;
    }
    let lastUserId: string | undefined;
    for (let i = timeline.length - 1; i >= 0; i--) {
      const item = timeline[i];
      if (item.kind === 'chat' && item.role === 'user') {
        lastUserId = item.id;
        break;
      }
    }
    if (!lastUserId) {
      return;
    }
    const el = userMessageAnchorsRef.current.get(lastUserId);
    if (!el) {
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const nextScrollTop = container.scrollTop + (elRect.top - containerRect.top);
    container.scrollTo({ top: Math.max(0, nextScrollTop), behavior: 'auto' });
  }, [scrollToTopAfterUserSend, timeline]);

  const hasContent = timeline.length > 0 || isFollowUpLoading;

  if (!hasContent) {
    return (
      <StyledContent>
        <AIOutreachEmptyState onGenerate={isError ? onRetry : onGenerate} isError={isError} />
      </StyledContent>
    );
  }

  return (
    <StyledOutreachContentRoot>
      <StyledOutreachScrollColumn ref={scrollColumnRef}>
        <AIOutreachTimeline
          items={timeline}
          showTyping={isFollowUpLoading}
          threadId={threadId}
          setUserMessageAnchor={setUserMessageAnchor}
        />
      </StyledOutreachScrollColumn>
    </StyledOutreachContentRoot>
  );
};
